/**
 * Resolver-route landing component. A reference chip links to one of the
 * resolver routes in paths.js (e.g. `/site-groups/42/`); this component
 * mounts there, resolves the entity's real destination page, and redirects.
 * Reference types with no destination (or a fetch that fails) land on the
 * 404 page instead of a broken redirect.
 *
 * Contains:
 * - EntityRedirect — resolver-route element, `type` passed via route props
 */
import React from 'react';
import { Navigate, useParams } from 'react-router-dom';

import RouteLoader from '@sophocles/components/App/RouteLoader';
import { useResolveRef } from '@sophocles/features/entities/references';
import Page404 from '@sophocles/views/errors/Page404';

import entityRedirectTarget, { REQUIRES_FETCH, RESOLVER_ROUTE } from './entityRedirectTarget';

/** Route param name carrying the id, per reference type — derived from each
 * resolver builder's own pattern rather than hand-duplicated. */
const PARAM_NAME = Object.fromEntries(
  Object.entries(RESOLVER_ROUTE).map(([type, builder]) => [type, builder.pattern.match(/:(\w+)/)[1]]),
);

/**
 * @param {{ type: string }} props - the reference type this route resolves;
 *   supplied by routes.js, one element per resolver route.
 */
function EntityRedirect({ type }) {
  const params = useParams();
  const id = params[PARAM_NAME[type]];
  const needsFetch = REQUIRES_FETCH.has(type);
  const { data: entity, isError } = useResolveRef(needsFetch ? { type, id } : null);

  if (needsFetch && !isError && entity === undefined) {
    return <RouteLoader showShell />;
  }
  if (needsFetch && isError) {
    return <Page404 />;
  }

  const target = entityRedirectTarget(type, id, entity);
  if (!target) {
    return <Page404 />;
  }

  return <Navigate to={target} replace />;
}

export default EntityRedirect;
